import { Component, ChangeDetectionStrategy, Output, EventEmitter, AfterViewInit, Input, ChangeDetectorRef, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { ChangeCatalogFilter, HandleCatalogHeaderToggle } from 'app/modules/data/modules/catalog-data/store/catalog-table.actions';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BooleanCatalogFilters, FilterName, HandleCatalogHeaderToggleModel } from 'app/modules/data/modules/catalog-data/models/catalog.service';
import { CatalogTableState } from 'app/modules/data/modules/catalog-data/store/catalog-table.state';
import { tap } from 'rxjs/operators';
import { AuthState } from '@auth/store/auth/auth.state';
import { AuthorizationDialogComponent } from '@auth/components/authorization-dialog/authorization-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { LangChangeEvent } from '@ngx-translate/core/lib/translate.service';
import { Locale } from '@shared/models/locale';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-catalog-table-filter',
  templateUrl: './catalog-table-filter.component.html',
  styleUrls: ['./catalog-table-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogTableFilterComponent implements OnInit, AfterViewInit {
  private _dataGroup = ['accessibility', 'summerSeason', 'winterSeason', 'allSeasons', 'withMap', 'withoutMap', 'iso'];
  private _catalogGroup = ['ordinaryDict', 'regionalDict', 'federalDict'];
  private _toggleDisableFields = [...this._dataGroup, ...this._catalogGroup];
  public filterForm = new UntypedFormGroup({
    newRecommendedFavorites: new UntypedFormControl('all'),
    datasetType: new UntypedFormControl(null),
    activeStatus: new UntypedFormControl(true),
    archiveStatus: new UntypedFormControl(false),
    accessibility: new UntypedFormControl(false),
    summerSeason: new UntypedFormControl(false),
    winterSeason: new UntypedFormControl(false),
    allSeasons: new UntypedFormControl(false),
    withMap: new UntypedFormControl(false),
    withoutMap: new UntypedFormControl(false),
    iso: new UntypedFormControl(false),
    ordinaryDict: new UntypedFormControl(false),
    regionalDict: new UntypedFormControl(false),
    federalDict: new UntypedFormControl(false)
  });
  public isRuLocale = true;
  public langChange$: Observable<LangChangeEvent>;
  public disableObserver$ = this.filterForm.get('datasetType')?.valueChanges.pipe(
    tap(val => {
      let fields: string[];
      switch (val) {
        case true:
          fields = this._catalogGroup;
          break;
        case false:
          fields = this._dataGroup;
          break;
        default:
          fields = this._toggleDisableFields;
      }

      Object.keys(this.filterForm.controls).forEach(key => {
        if (this._toggleDisableFields.includes(key)) {
          if (fields.includes(key)) {
            (this.filterForm.controls[key] as UntypedFormControl).enable({ emitEvent: false });
          } else {
            (this.filterForm.controls[key] as UntypedFormControl).reset(false, { emitEvent: false });
            (this.filterForm.controls[key] as UntypedFormControl).disable({ emitEvent: false });
          }
        }
      });
    })
  );

  @Input() public filterContainer: 'modal-filters-container' | 'filter-container' = 'filter-container';
  @Input() public isMobile = false;
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public readonly close = new EventEmitter<boolean>();

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  private onNewFavoriteRecommendedChange(type: HandleCatalogHeaderToggleModel): void {
    const user = this.store.selectSnapshot(AuthState.authenticatedUser);

    if (!user && type !== 'new' && type !== 'all') {
      const authorizationDialog = this.dialog.open(AuthorizationDialogComponent, { disableClose: true });
      authorizationDialog.componentInstance.keyOperations = 'NewRecommendedFavoriteToggleState';
      authorizationDialog.componentInstance.operationValue = { type };
      return;
    }

    this.store.dispatch(new HandleCatalogHeaderToggle(type));
  }

  private setText(lang: Locale): void {
    if (lang === Locale.RU_RU) {
      this.isRuLocale = true;
    } else {
      this.isRuLocale = false;
    }
  }

  public ngOnInit(): void {
    this.setText(this.translateService.currentLang as Locale);
    this.langChange$ = this.translateService.onLangChange.pipe(
      tap(res => {
        this.setText(res.lang as Locale);
      })
    );
  }

  public ngAfterViewInit(): void {
    const allFilters = this.store.selectSnapshot(CatalogTableState.getCatalogFilter);
    const newRecommendedFavorites = allFilters?.newRecommendedFavorites;
    if (newRecommendedFavorites) {
      this.filterForm.get('newRecommendedFavorites')?.setValue(newRecommendedFavorites);
    }
    const filterBooleanParams = allFilters?.booleanFilters;
    const params = { ...(filterBooleanParams || this.route.snapshot.queryParams) };

    Object.keys(params).forEach(key => {
      if (key === FilterName.DataSetType) {
        if (params[key]) {
          if (params[key] === true || params[key] === 'true') {
            params[key] = true;
          }
          if (params[key] === false || params[key] === 'false') {
            params[key] = false;
          }
          if (params[key] === null || params[key] === 'null') {
            params[key] = null;
          }
        }
      }
      this.filterForm.get(key)?.setValue(params[key]);
    });
    this.cdr.detectChanges();
  }

  public convertFormValueToPayload(): BooleanCatalogFilters {
    const booleanFilters: BooleanCatalogFilters = {};

    Object.keys(this.filterForm.controls).forEach(key => {
      if (key !== FilterName.NewRecommendedFavorites) {
        const control = this.filterForm.get(key);

        if (!control?.disabled) {
          if (key !== FilterName.DataSetType) {
            booleanFilters[key] = control?.value || null;
          } else {
            booleanFilters.datasetType = this.filterForm.get(key)?.value;
          }
        } else {
          booleanFilters[key] = null;
        }
      }
    });

    return booleanFilters;
  }

  public onSubmit(): void {
    const newRecommendedFavorites = this.filterForm.get('newRecommendedFavorites')?.value as HandleCatalogHeaderToggleModel;
    if (newRecommendedFavorites && newRecommendedFavorites !== 'all') {
      this.store.dispatch(
        new ChangeCatalogFilter({ newRecommendedFavorites: newRecommendedFavorites, booleanFilters: this.convertFormValueToPayload() }, false)
      );
      setTimeout(() => {
        this.onNewFavoriteRecommendedChange(newRecommendedFavorites);
      });
    } else {
      this.store.dispatch(
        new ChangeCatalogFilter({ newRecommendedFavorites: newRecommendedFavorites, booleanFilters: this.convertFormValueToPayload() }, true)
      );
    }

    this.close.emit(true);
  }

  public reset(): void {
    if (this.isMobile) {
      this.store.dispatch(new HandleCatalogHeaderToggle('all', false));
    }

    this.filterForm.reset();
    this.store.dispatch(
      new ChangeCatalogFilter(
        {
          booleanFilters: {
            activeStatus: true,
            archiveStatus: null,
            withMap: null,
            withoutMap: null,
            iso: null,
            summerSeason: null,
            regionalDict: null,
            ordinaryDict: null,
            federalDict: null,
            datasetType: null,
            allSeasons: null,
            accessibility: null,
            winterSeason: null
          }
        },
        true
      )
    );
    this.close.emit(true);
  }

  public chooseMapFilter(oppositeFilterName: string): void {
    this.filterForm.get(oppositeFilterName)?.patchValue(false);
  }
}
